<h1 mat-dialog-title>{{ 'MASSIVE_BID.TITLE' | translate }}</h1>

<mat-dialog-content>
  <!-- FORM -->
  <ng-container *ngIf="!hasResults">
    <form [formGroup]="massiveBidFormGroup" fxLayout="column" fxLayoutGap="16px" class="line-height-1.75">
      <div fxLayout="column" fxLayoutGap="8px">
        <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="10px">
          <span><b>{{ 'MASSIVE_BID.FUND' | translate }}:</b></span>
          <button mat-button class="fund-button" [matMenuTriggerFor]="fundMenu">
            <mat-icon class="mr-4">account_balance_wallet</mat-icon>
            <span [matTooltip]="selectedFund?.name">{{ selectedFund?.name }}</span>
            <mat-icon>arrow_drop_down</mat-icon>
          </button>
          <mat-menu #fundMenu>
            <button *ngFor="let fund of buyer?.funds" mat-menu-item (click)="selectFund(fund)" [matTooltip]="fund.name">
              <span>{{ fund.name }}</span>
            </button>
          </mat-menu>
        </div>

        <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="16px">
          <span *ngIf="virtualBalance">
            <b>{{ 'MASSIVE_BID.VIRTUAL_BALANCE' | translate }}:</b>
            {{ virtualBalance.virtualBalance | currency: 'EUR' }}
          </span>
          <span>
            <b>{{ 'MASSIVE_BID.TOTAL_DOWN_PAYMENT' | translate }}:</b>
            {{ totalDownPayment | currency: 'EUR' }}
          </span>
        </div>
      </div>

      <mat-card *ngIf="hasInsufficientLiquidity" class="p-16 mat-elevation-z0 warning-card">
        <mat-card-content>
          <mat-icon class="s-24 mr-8 warning-icon">warning</mat-icon>
          <span>{{ 'MASSIVE_BID.ALERT_INSUFFICIENT_LIQUIDITY' | translate }}</span>
        </mat-card-content>
      </mat-card>

      <mat-divider></mat-divider>

      <!-- MASTER RATE -->
      <div fxLayout="row" fxLayout.lt-md="column" fxLayoutAlign="start center" fxLayoutGap="16px">
        <mat-form-field fxFlex="240px" appearance="outline">
          <mat-label>{{ 'MASSIVE_BID.MASTER_RATE' | translate }} %</mat-label>
          <input matInput type="number" [formControl]="masterRate" [step]="stepPercent" min="0" />
        </mat-form-field>
        <button mat-stroked-button
                type="button"
                [disabled]="isSending || masterRate.value === null || masterRate.value === ''"
                (click)="applyMasterRate()">
          {{ 'MASSIVE_BID.APPLY_TO_ALL' | translate }}
        </button>
        <span class="text-secondary">{{ 'MASSIVE_BID.MASTER_RATE_HINT' | translate }}</span>
      </div>
      <!-- / MASTER RATE -->

      <!-- AUCTIONS -->
      <div class="massive-bid-table-wrapper" formArrayName="rates">
        <table class="massive-bid-table">
          <colgroup>
            <col class="col-id" />
            <col class="col-business-name" />
            <col class="col-business-name" />
            <col class="col-amount" />
            <col class="col-amount" />
            <col class="col-rate" />
            <col class="col-rate" />
            <col class="col-actions" />
          </colgroup>
          <thead>
          <tr>
            <th>{{ 'TABLE.ID' | translate }}</th>
            <th>{{ 'TABLE.SELLER_BUSINESS_NAME' | translate }}</th>
            <th>{{ 'TABLE.DEBTOR_BUSINESS_NAME' | translate }}</th>
            <th class="text-right">{{ 'TABLE.INVOICE_AMOUNT' | translate }}</th>
            <th class="text-right">{{ 'MASSIVE_BID.DOWN_PAYMENT' | translate }}</th>
            <th class="text-right">{{ 'MASSIVE_BID.MAX_RATE' | translate }}</th>
            <th class="text-right">{{ 'MASSIVE_BID.BID_RATE' | translate }} %</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr *ngFor="let auction of auctions; let i = index" [formGroupName]="i">
            <td>{{ auction.id }}</td>
            <td class="ellipsis-cell" [matTooltip]="auction.sellerBusinessName">
              {{ auction.sellerBusinessName }}
            </td>
            <td class="ellipsis-cell" [matTooltip]="auction.debtorBusinessName">
              {{ auction.debtorBusinessName }}
            </td>
            <td class="text-right">{{ auction.invoiceAmount | currency: 'EUR' }}</td>
            <td class="text-right">{{ downPaymentAmount(auction) | currency: 'EUR' }}</td>
            <td class="text-right">
              {{ auction.maxBidInterestRate || auction.nominalMaxInterestRate | percent: '0.0-3' }}
            </td>
            <td class="text-right rate-cell">
              <mat-form-field class="rate-field">
                <input matInput
                       type="number"
                       formControlName="bidInterestRate"
                       [step]="stepPercent"
                       required />
                <mat-error>
                  <span *ngIf="rateGroup(auction.id).get('bidInterestRate').hasError('required')">
                    {{ 'ERROR.REQUIRED' | translate }}
                  </span>
                  <span *ngIf="rateGroup(auction.id).get('bidInterestRate').hasError('max')">
                    {{ 'ERROR.HIGH' | translate }}
                  </span>
                </mat-error>
              </mat-form-field>
              <span *ngIf="auction.invoiceIasCompliant"
                    class="text-secondary ias-hint"
                    [matTooltip]="'MASSIVE_BID.IAS_COMPLIANT_INVOICE' | translate">*</span>
            </td>
            <td>
              <button mat-icon-button
                      type="button"
                      [matTooltip]="'MASSIVE_BID.DOWNLOAD_CONDITIONS' | translate"
                      [disabled]="!selectedFund || rateGroup(auction.id).get('bidInterestRate').invalid"
                      (click)="downloadConditions(auction)">
                <mat-icon>description</mat-icon>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <!-- / AUCTIONS -->

      <mat-divider *ngIf="showPassword"></mat-divider>

      <div *ngIf="showPassword" fxLayout="row">
        <mat-form-field fxFlex="320px">
          <mat-label>{{ 'MASSIVE_BID.TRANSACTION_PASSWORD' | translate }}</mat-label>
          <input matInput type="password" formControlName="transactionPassword" required />
          <mat-error>
            <span *ngIf="massiveBidFormGroup.get('transactionPassword').hasError('forbidden')">
              {{ 'ERROR.WRONG_TRANSACTION_PASSWORD' | translate }}
            </span>
            <span *ngIf="massiveBidFormGroup.get('transactionPassword').hasError('required')">
              {{ 'ERROR.REQUIRED' | translate }}
            </span>
          </mat-error>
        </mat-form-field>
      </div>

      <mat-divider></mat-divider>

      <div fxLayout="column" fxLayoutAlign="start start" fxLayoutGap="10px">
        <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="10px">
          <mat-checkbox formControlName="tos_1"></mat-checkbox>
          <div style="padding-top: 3px;">
            <span>{{ 'MASSIVE_BID.TOS_1' | translate }}</span>
            <a href="assets/pdf/b_conditions.pdf" target="_blank">{{ 'MASSIVE_BID.TOS_1_LINK' | translate }}</a>.
          </div>
        </div>

        <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="10px">
          <mat-checkbox formControlName="tos_2"></mat-checkbox>
          <div style="padding-top: 3px;">
            <span>{{ 'MASSIVE_BID.TOS_2' | translate }}</span>
          </div>
        </div>

        <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="10px">
          <mat-checkbox formControlName="tos_3"></mat-checkbox>
          <div style="padding-top: 3px;">
            <span>{{ 'MASSIVE_BID.TOS_3' | translate }}</span>
          </div>
        </div>

        <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="10px">
          <mat-checkbox formControlName="tos_4"></mat-checkbox>
          <div style="padding-top: 3px;">
            <span>{{ 'MASSIVE_BID.TOS_4' | translate }}</span>
          </div>
        </div>
      </div>
    </form>
  </ng-container>
  <!-- / FORM -->

  <!-- RESULTS -->
  <ng-container *ngIf="hasResults">
    <div fxLayout="column" fxLayoutGap="16px">
      <h3>{{ 'MASSIVE_BID.RESULT_SUMMARY' | translate: { success: successCount, total: results.length } }}</h3>

      <div class="massive-bid-table-wrapper">
        <table class="massive-bid-table">
          <colgroup>
            <col class="col-id" />
            <col class="col-business-name" />
            <col class="col-narrow" />
            <col class="col-outcome" />
          </colgroup>
          <thead>
          <tr>
            <th>{{ 'TABLE.ID' | translate }}</th>
            <th>{{ 'TABLE.SELLER_BUSINESS_NAME' | translate }}</th>
            <th>{{ 'TABLE.INVOICE_NUMBER' | translate }}</th>
            <th>{{ 'MASSIVE_BID.OUTCOME' | translate }}</th>
          </tr>
          </thead>
          <tbody>
          <tr *ngFor="let result of results">
            <td>{{ result.item }}</td>
            <td class="ellipsis-cell" [matTooltip]="auctionById(result.item)?.sellerBusinessName">{{ auctionById(result.item)?.sellerBusinessName }}</td>
            <td>{{ auctionById(result.item)?.invoiceNumber }}</td>
            <td>
              <span *ngIf="result.success" class="green-600-fg" fxLayout="row" fxLayoutAlign="start center">
                <mat-icon class="s-20 mr-4">check_circle</mat-icon>
                {{ 'MASSIVE_BID.OUTCOME_SUCCESS' | translate }}
              </span>
              <span *ngIf="!result.success" class="red-600-fg" fxLayout="row" fxLayoutAlign="start center">
                <mat-icon class="s-20 mr-4">error_outline</mat-icon>
                {{ errorLabel(result.errorMessage) }}
              </span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </ng-container>
  <!-- / RESULTS -->
</mat-dialog-content>

<mat-dialog-actions align="end">
  <ng-container *ngIf="!hasResults">
    <button mat-button [disabled]="isSending" [mat-dialog-close]="false">
      <span>{{ 'MASSIVE_BID.CANCEL' | translate }}</span>
    </button>
    <button mat-button
            color="accent"
            [disabled]="massiveBidFormGroup.invalid || isSending || !selectedFund"
            (click)="submitMassiveBid()">
      <span>{{ 'MASSIVE_BID.SEND' | translate: { count: auctions.length } }}</span>
    </button>
  </ng-container>

  <button *ngIf="hasResults" mat-button color="accent" (click)="close()">
    <span>{{ 'MASSIVE_BID.CLOSE' | translate }}</span>
  </button>
</mat-dialog-actions>
